"use client";

import { useEffect, useMemo, useState } from "react";
import { DatePickerComponent } from "@/components/DatePickerComponent";
import { ComboSearch } from "@/components/ComboSearch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  OrderDeliveryType,
  OrderPaymentStatus,
  OrderStatus,
  type TagsOutput,
  orderDeliveryTypeTransalation,
  orderPaymentStatusTranslation,
  orderStatusTranslation,
} from "@/interfaces";
import { TagsService } from "@/services/TagService";
import { cn } from "@/lib/utils";
import {
  ChevronDown,
  ChevronUp,
  FilterX,
  SlidersHorizontal,
} from "lucide-react";

export interface OrdersPageFilters {
  customerId?: string;
  status?: OrderStatus;
  paymentStatus?: OrderPaymentStatus;
  deliveryType?: OrderDeliveryType;
  startOrderDate?: Date;
  endOrderDate?: Date;
  tags?: string[];
}

interface OrdersFilterBarProps {
  filters: OrdersPageFilters;
  onChange: (filters: OrdersPageFilters) => void;
}

const ALL_OPTION = "all";

export function OrdersFilterBar({
  filters,
  onChange,
}: Readonly<OrdersFilterBarProps>) {
  const [availableTags, setAvailableTags] = useState<TagsOutput[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const loadTags = async () => {
      try {
        const service = new TagsService();
        const response = await service.findAll({ page: 1, pageSize: 100 });
        setAvailableTags(response.data);
      } catch (error) {
        console.error("Erro ao buscar tags para filtros:", error);
      }
    };

    loadTags();
  }, []);

  const activeFiltersCount = useMemo(() => {
    return [
      filters.customerId,
      filters.status,
      filters.paymentStatus,
      filters.deliveryType,
      filters.startOrderDate,
      filters.endOrderDate,
      filters.tags?.length ? filters.tags.join(",") : undefined,
    ].filter(Boolean).length;
  }, [filters]);

  const updateFilter = <K extends keyof OrdersPageFilters>(
    key: K,
    value: OrdersPageFilters[K]
  ) => {
    const nextFilters = { ...filters };

    if (
      value === undefined ||
      value === null ||
      (Array.isArray(value) && value.length === 0)
    ) {
      delete nextFilters[key];
    } else {
      nextFilters[key] = value;
    }

    onChange(nextFilters);
  };

  const toggleTag = (tagId: string) => {
    const currentTags = filters.tags ?? [];
    const nextTags = currentTags.includes(tagId)
      ? currentTags.filter((id) => id !== tagId)
      : [...currentTags, tagId];

    updateFilter("tags", nextTags.length ? nextTags : undefined);
  };

  const clearFilters = () => {
    onChange({});
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-950"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="h-auto justify-start px-0 py-0 text-left hover:bg-transparent"
          >
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              <SlidersHorizontal className="h-4 w-4 text-[#EE7B11]" />
              Filtros de pedidos
              {isOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </div>
          </Button>
        </CollapsibleTrigger>

        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="border-orange-200 bg-orange-50 text-orange-700"
          >
            {activeFiltersCount} filtro(s) ativo(s)
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            disabled={activeFiltersCount === 0}
            className="text-gray-600 hover:text-[#EE7B11]"
          >
            <FilterX className="mr-2 h-4 w-4" />
            Limpar filtros
          </Button>
        </div>
      </div>

      <CollapsibleContent className="mt-5 space-y-5">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-12">
        <div className="xl:col-span-4">
          <p className="mb-1 px-1 text-sm font-medium text-gray-600">Cliente</p>
          <ComboSearch
            id="orders-customer-filter"
            context="customer"
            value={filters.customerId ?? ""}
            onSelect={(value) => updateFilter("customerId", value || undefined)}
            placeholder="Buscar cliente"
            className="w-full"
            maxWidth="100%"
          />
        </div>

        <div className="xl:col-span-2">
          <p className="mb-1 px-1 text-sm font-medium text-gray-600">
            Status do Pedido
          </p>
          <Select
            value={filters.status ?? ALL_OPTION}
            onValueChange={(value) =>
              updateFilter(
                "status",
                value === ALL_OPTION ? undefined : (value as OrderStatus)
              )
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_OPTION}>Todos</SelectItem>
              {Object.entries(orderStatusTranslation).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="xl:col-span-2">
          <p className="mb-1 px-1 text-sm font-medium text-gray-600">
            Status de Pagamento
          </p>
          <Select
            value={filters.paymentStatus ?? ALL_OPTION}
            onValueChange={(value) =>
              updateFilter(
                "paymentStatus",
                value === ALL_OPTION
                  ? undefined
                  : (value as OrderPaymentStatus)
              )
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_OPTION}>Todos</SelectItem>
              {Object.entries(orderPaymentStatusTranslation).map(
                ([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="xl:col-span-2">
          <p className="mb-1 px-1 text-sm font-medium text-gray-600">
            Tipo de Entrega
          </p>
          <Select
            value={filters.deliveryType ?? ALL_OPTION}
            onValueChange={(value) =>
              updateFilter(
                "deliveryType",
                value === ALL_OPTION
                  ? undefined
                  : (value as OrderDeliveryType)
              )
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_OPTION}>Todos</SelectItem>
              {Object.entries(orderDeliveryTypeTransalation).map(
                ([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="xl:col-span-2">
          <p className="mb-1 px-1 text-sm font-medium text-gray-600">
            Data Inicial
          </p>
          <DatePickerComponent
            selected={filters.startOrderDate}
            onSelect={(date) => updateFilter("startOrderDate", date)}
            className="w-full"
          />
        </div>

        <div className="xl:col-span-2">
          <p className="mb-1 px-1 text-sm font-medium text-gray-600">
            Data Final
          </p>
          <DatePickerComponent
            selected={filters.endOrderDate}
            onSelect={(date) => updateFilter("endOrderDate", date)}
            className="w-full"
          />
        </div>
      </div>

      {availableTags.length > 0 && (
        <div className="space-y-2">
          <p className="px-1 text-sm font-medium text-gray-600">Tags</p>
          <div className="flex flex-wrap gap-2">
            {availableTags.map((tag) => {
              const isActive = (filters.tags ?? []).includes(tag.id);

              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    isActive
                      ? "border-[#EE7B11] bg-[#EE7B11] text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:border-orange-300 hover:text-[#EE7B11]"
                  )}
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
      </CollapsibleContent>
    </Collapsible>
  );
}
